/**
 * Tailwind CSS Class Database
 *
 * Generates a comprehensive list of Tailwind utility classes for autocomplete.
 * Reuses color palette from tailwind-colors.ts for color-based classes.
 */

import {
  TAILWIND_COLOR_ORDER,
  TAILWIND_SHADES,
} from "@/components/editor/themes/utils/tailwind-colors";

// ============================================================================
// Class generation from patterns
// ============================================================================

const SPECIAL_COLORS = ["white", "black", "transparent", "current", "inherit"];

const COLOR_PREFIXES = [
  "text",
  "bg",
  "border",
  "ring",
  "ring-offset",
  "outline",
  "accent",
  "decoration",
  "shadow",
  "divide",
  "from",
  "via",
  "to",
  "caret",
  "fill",
  "stroke",
  "placeholder",
];

const SPACING_SIZES = [
  "0",
  "px",
  "0.5",
  "1",
  "1.5",
  "2",
  "2.5",
  "3",
  "3.5",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "11",
  "12",
  "14",
  "16",
  "20",
  "24",
  "28",
  "32",
  "36",
  "40",
  "44",
  "48",
  "52",
  "56",
  "60",
  "64",
  "72",
  "80",
  "96",
];

const SPACING_PREFIXES = [
  "m",
  "mx",
  "my",
  "mt",
  "mr",
  "mb",
  "ml",
  "ms",
  "me",
  "p",
  "px",
  "py",
  "pt",
  "pr",
  "pb",
  "pl",
  "ps",
  "pe",
  "gap",
  "gap-x",
  "gap-y",
  "space-x",
  "space-y",
  "inset",
  "inset-x",
  "inset-y",
  "top",
  "right",
  "bottom",
  "left",
  "start",
  "end",
  "scroll-m",
  "scroll-mx",
  "scroll-my",
  "scroll-mt",
  "scroll-mr",
  "scroll-mb",
  "scroll-ml",
  "scroll-ms",
  "scroll-me",
  "scroll-p",
  "scroll-px",
  "scroll-py",
  "scroll-pt",
  "scroll-pr",
  "scroll-pb",
  "scroll-pl",
  "scroll-ps",
  "scroll-pe",
  "indent",
];

const TEXT_SIZES = [
  "xs",
  "sm",
  "base",
  "lg",
  "xl",
  "2xl",
  "3xl",
  "4xl",
  "5xl",
  "6xl",
  "7xl",
  "8xl",
  "9xl",
];

const FONT_WEIGHTS = [
  "thin",
  "extralight",
  "light",
  "normal",
  "medium",
  "semibold",
  "bold",
  "extrabold",
  "black",
];

const WIDTH_SIZES = [
  ...SPACING_SIZES,
  "auto",
  "full",
  "screen",
  "svw",
  "lvw",
  "dvw",
  "min",
  "max",
  "fit",
  "1/2",
  "1/3",
  "2/3",
  "1/4",
  "2/4",
  "3/4",
  "1/5",
  "2/5",
  "3/5",
  "4/5",
  "1/6",
  "2/6",
  "3/6",
  "4/6",
  "5/6",
  "1/12",
  "2/12",
  "3/12",
  "4/12",
  "5/12",
  "6/12",
  "7/12",
  "8/12",
  "9/12",
  "10/12",
  "11/12",
];

const HEIGHT_SIZES = [
  ...SPACING_SIZES,
  "auto",
  "full",
  "screen",
  "svh",
  "lvh",
  "dvh",
  "min",
  "max",
  "fit",
  "1/2",
  "1/3",
  "2/3",
  "1/4",
  "2/4",
  "3/4",
  "1/5",
  "2/5",
  "3/5",
  "4/5",
  "1/6",
  "2/6",
  "3/6",
  "4/6",
  "5/6",
];

const BORDER_RADIUS = [
  "none",
  "sm",
  "",
  "md",
  "lg",
  "xl",
  "2xl",
  "3xl",
  "full",
];

const OPACITY_VALUES = [
  "0",
  "5",
  "10",
  "15",
  "20",
  "25",
  "30",
  "35",
  "40",
  "45",
  "50",
  "55",
  "60",
  "65",
  "70",
  "75",
  "80",
  "85",
  "90",
  "95",
  "100",
];

const Z_INDEX = ["0", "10", "20", "30", "40", "50", "auto"];

// ============================================================================
// Static utility classes (non-parameterized)
// ============================================================================

const STATIC_CLASSES = [
  // Box sizing
  "box-border",
  "box-content",
  // Container
  "container",
  // Display
  "block",
  "inline-block",
  "inline",
  "flex",
  "inline-flex",
  "grid",
  "inline-grid",
  "table",
  "inline-table",
  "table-caption",
  "table-cell",
  "table-column",
  "table-column-group",
  "table-footer-group",
  "table-header-group",
  "table-row-group",
  "table-row",
  "contents",
  "flow-root",
  "list-item",
  "hidden",
  // Position
  "static",
  "fixed",
  "absolute",
  "relative",
  "sticky",
  // Float / Clear
  "float-start",
  "float-end",
  "float-right",
  "float-left",
  "float-none",
  "clear-start",
  "clear-end",
  "clear-left",
  "clear-right",
  "clear-both",
  "clear-none",
  // Isolation
  "isolate",
  "isolation-auto",
  // Overflow
  "overflow-auto",
  "overflow-hidden",
  "overflow-clip",
  "overflow-visible",
  "overflow-scroll",
  "overflow-x-auto",
  "overflow-x-hidden",
  "overflow-x-clip",
  "overflow-x-visible",
  "overflow-x-scroll",
  "overflow-y-auto",
  "overflow-y-hidden",
  "overflow-y-clip",
  "overflow-y-visible",
  "overflow-y-scroll",
  // Overscroll
  "overscroll-auto",
  "overscroll-contain",
  "overscroll-none",
  "overscroll-x-auto",
  "overscroll-x-contain",
  "overscroll-x-none",
  "overscroll-y-auto",
  "overscroll-y-contain",
  "overscroll-y-none",
  // Flex
  "flex-row",
  "flex-row-reverse",
  "flex-col",
  "flex-col-reverse",
  "flex-wrap",
  "flex-wrap-reverse",
  "flex-nowrap",
  "flex-1",
  "flex-auto",
  "flex-initial",
  "flex-none",
  "grow",
  "grow-0",
  "shrink",
  "shrink-0",
  "basis-auto",
  "basis-full",
  // Order
  "order-1",
  "order-2",
  "order-3",
  "order-4",
  "order-5",
  "order-6",
  "order-7",
  "order-8",
  "order-9",
  "order-10",
  "order-11",
  "order-12",
  "order-first",
  "order-last",
  "order-none",
  // Columns
  "columns-1",
  "columns-2",
  "columns-3",
  "columns-4",
  "columns-5",
  "columns-6",
  "columns-7",
  "columns-8",
  "columns-9",
  "columns-10",
  "columns-11",
  "columns-12",
  "columns-auto",
  "columns-3xs",
  "columns-2xs",
  "columns-xs",
  "columns-sm",
  "columns-md",
  "columns-lg",
  "columns-xl",
  "columns-2xl",
  "columns-3xl",
  "columns-4xl",
  "columns-5xl",
  "columns-6xl",
  "columns-7xl",
  // Break after/before/inside
  "break-after-auto",
  "break-after-avoid",
  "break-after-all",
  "break-after-avoid-page",
  "break-after-page",
  "break-after-left",
  "break-after-right",
  "break-after-column",
  "break-before-auto",
  "break-before-avoid",
  "break-before-all",
  "break-before-avoid-page",
  "break-before-page",
  "break-before-left",
  "break-before-right",
  "break-before-column",
  "break-inside-auto",
  "break-inside-avoid",
  "break-inside-avoid-page",
  "break-inside-avoid-column",
  // Box decoration
  "box-decoration-clone",
  "box-decoration-slice",
  // Align / Justify
  "items-start",
  "items-end",
  "items-center",
  "items-baseline",
  "items-stretch",
  "justify-start",
  "justify-end",
  "justify-center",
  "justify-between",
  "justify-around",
  "justify-evenly",
  "justify-stretch",
  "justify-items-start",
  "justify-items-end",
  "justify-items-center",
  "justify-items-stretch",
  "self-auto",
  "self-start",
  "self-end",
  "self-center",
  "self-stretch",
  "self-baseline",
  "justify-self-auto",
  "justify-self-start",
  "justify-self-end",
  "justify-self-center",
  "justify-self-stretch",
  "content-start",
  "content-end",
  "content-center",
  "content-between",
  "content-around",
  "content-evenly",
  "content-stretch",
  "place-content-start",
  "place-content-end",
  "place-content-center",
  "place-content-between",
  "place-content-around",
  "place-content-evenly",
  "place-items-start",
  "place-items-end",
  "place-items-center",
  "place-items-stretch",
  "place-self-auto",
  "place-self-start",
  "place-self-end",
  "place-self-center",
  "place-self-stretch",
  // Grid
  "grid-cols-1",
  "grid-cols-2",
  "grid-cols-3",
  "grid-cols-4",
  "grid-cols-5",
  "grid-cols-6",
  "grid-cols-7",
  "grid-cols-8",
  "grid-cols-9",
  "grid-cols-10",
  "grid-cols-11",
  "grid-cols-12",
  "grid-cols-none",
  "grid-cols-subgrid",
  "grid-rows-1",
  "grid-rows-2",
  "grid-rows-3",
  "grid-rows-4",
  "grid-rows-5",
  "grid-rows-6",
  "grid-rows-none",
  "grid-rows-subgrid",
  "col-span-1",
  "col-span-2",
  "col-span-3",
  "col-span-4",
  "col-span-5",
  "col-span-6",
  "col-span-7",
  "col-span-8",
  "col-span-9",
  "col-span-10",
  "col-span-11",
  "col-span-12",
  "col-span-full",
  "col-auto",
  "col-start-1",
  "col-start-2",
  "col-start-3",
  "col-start-4",
  "col-start-5",
  "col-start-6",
  "col-start-7",
  "col-start-8",
  "col-start-9",
  "col-start-10",
  "col-start-11",
  "col-start-12",
  "col-start-13",
  "col-start-auto",
  "col-end-1",
  "col-end-2",
  "col-end-3",
  "col-end-4",
  "col-end-5",
  "col-end-6",
  "col-end-7",
  "col-end-8",
  "col-end-9",
  "col-end-10",
  "col-end-11",
  "col-end-12",
  "col-end-13",
  "col-end-auto",
  "row-span-1",
  "row-span-2",
  "row-span-3",
  "row-span-4",
  "row-span-5",
  "row-span-6",
  "row-span-full",
  "row-auto",
  "row-start-1",
  "row-start-2",
  "row-start-3",
  "row-start-4",
  "row-start-5",
  "row-start-6",
  "row-start-7",
  "row-start-auto",
  "row-end-1",
  "row-end-2",
  "row-end-3",
  "row-end-4",
  "row-end-5",
  "row-end-6",
  "row-end-7",
  "row-end-auto",
  "auto-cols-auto",
  "auto-cols-min",
  "auto-cols-max",
  "auto-cols-fr",
  "auto-rows-auto",
  "auto-rows-min",
  "auto-rows-max",
  "auto-rows-fr",
  // Grid flow
  "grid-flow-row",
  "grid-flow-col",
  "grid-flow-dense",
  "grid-flow-row-dense",
  "grid-flow-col-dense",
  // Font family
  "font-sans",
  "font-serif",
  "font-mono",
  // Text
  "text-left",
  "text-center",
  "text-right",
  "text-justify",
  "text-start",
  "text-end",
  "text-wrap",
  "text-nowrap",
  "text-balance",
  "text-pretty",
  "truncate",
  "text-ellipsis",
  "text-clip",
  "break-normal",
  "break-words",
  "break-all",
  "break-keep",
  "hyphens-none",
  "hyphens-manual",
  "hyphens-auto",
  // Font style
  "italic",
  "not-italic",
  "uppercase",
  "lowercase",
  "capitalize",
  "normal-case",
  "underline",
  "overline",
  "line-through",
  "no-underline",
  "antialiased",
  "subpixel-antialiased",
  // Text decoration style
  "decoration-solid",
  "decoration-double",
  "decoration-dashed",
  "decoration-dotted",
  "decoration-wavy",
  "decoration-auto",
  "decoration-from-font",
  "decoration-0",
  "decoration-1",
  "decoration-2",
  "decoration-4",
  "decoration-8",
  // Underline offset
  "underline-offset-auto",
  "underline-offset-0",
  "underline-offset-1",
  "underline-offset-2",
  "underline-offset-4",
  "underline-offset-8",
  // Leading (line-height)
  "leading-none",
  "leading-tight",
  "leading-snug",
  "leading-normal",
  "leading-relaxed",
  "leading-loose",
  "leading-3",
  "leading-4",
  "leading-5",
  "leading-6",
  "leading-7",
  "leading-8",
  "leading-9",
  "leading-10",
  // Tracking (letter-spacing)
  "tracking-tighter",
  "tracking-tight",
  "tracking-normal",
  "tracking-wide",
  "tracking-wider",
  "tracking-widest",
  // Whitespace
  "whitespace-normal",
  "whitespace-nowrap",
  "whitespace-pre",
  "whitespace-pre-line",
  "whitespace-pre-wrap",
  "whitespace-break-spaces",
  // Word break
  "break-normal",
  "break-words",
  "break-all",
  "break-keep",
  // Vertical align
  "align-baseline",
  "align-top",
  "align-middle",
  "align-bottom",
  "align-text-top",
  "align-text-bottom",
  "align-sub",
  "align-super",
  // List style
  "list-none",
  "list-disc",
  "list-decimal",
  "list-inside",
  "list-outside",
  "list-image-none",
  // Table layout
  "table-auto",
  "table-fixed",
  "border-collapse",
  "border-separate",
  "border-spacing-0",
  "border-spacing-px",
  "border-spacing-1",
  "border-spacing-2",
  "border-spacing-3",
  "border-spacing-4",
  "border-spacing-5",
  "border-spacing-6",
  "border-spacing-8",
  "border-spacing-x-0",
  "border-spacing-x-px",
  "border-spacing-x-1",
  "border-spacing-x-2",
  "border-spacing-x-4",
  "border-spacing-x-8",
  "border-spacing-y-0",
  "border-spacing-y-px",
  "border-spacing-y-1",
  "border-spacing-y-2",
  "border-spacing-y-4",
  "border-spacing-y-8",
  "caption-top",
  "caption-bottom",
  // Border style
  "border",
  "border-0",
  "border-2",
  "border-4",
  "border-8",
  "border-x",
  "border-x-0",
  "border-x-2",
  "border-x-4",
  "border-x-8",
  "border-y",
  "border-y-0",
  "border-y-2",
  "border-y-4",
  "border-y-8",
  "border-t",
  "border-t-0",
  "border-t-2",
  "border-t-4",
  "border-t-8",
  "border-r",
  "border-r-0",
  "border-r-2",
  "border-r-4",
  "border-r-8",
  "border-b",
  "border-b-0",
  "border-b-2",
  "border-b-4",
  "border-b-8",
  "border-l",
  "border-l-0",
  "border-l-2",
  "border-l-4",
  "border-l-8",
  "border-solid",
  "border-dashed",
  "border-dotted",
  "border-double",
  "border-hidden",
  "border-none",
  // Divide
  "divide-x",
  "divide-x-0",
  "divide-x-2",
  "divide-x-4",
  "divide-x-8",
  "divide-y",
  "divide-y-0",
  "divide-y-2",
  "divide-y-4",
  "divide-y-8",
  "divide-x-reverse",
  "divide-y-reverse",
  "divide-solid",
  "divide-dashed",
  "divide-dotted",
  "divide-double",
  "divide-none",
  // Ring offset
  "ring-offset-0",
  "ring-offset-1",
  "ring-offset-2",
  "ring-offset-4",
  "ring-offset-8",
  // Shadows
  "shadow-sm",
  "shadow",
  "shadow-md",
  "shadow-lg",
  "shadow-xl",
  "shadow-2xl",
  "shadow-inner",
  "shadow-none",
  // Ring
  "ring",
  "ring-0",
  "ring-1",
  "ring-2",
  "ring-4",
  "ring-8",
  "ring-inset",
  // Outline
  "outline-none",
  "outline",
  "outline-dashed",
  "outline-dotted",
  "outline-double",
  "outline-0",
  "outline-1",
  "outline-2",
  "outline-4",
  "outline-8",
  "outline-offset-0",
  "outline-offset-1",
  "outline-offset-2",
  "outline-offset-4",
  "outline-offset-8",
  // Background
  "bg-fixed",
  "bg-local",
  "bg-scroll",
  "bg-clip-border",
  "bg-clip-padding",
  "bg-clip-content",
  "bg-clip-text",
  "bg-repeat",
  "bg-no-repeat",
  "bg-repeat-x",
  "bg-repeat-y",
  "bg-repeat-round",
  "bg-repeat-space",
  "bg-origin-border",
  "bg-origin-padding",
  "bg-origin-content",
  "bg-auto",
  "bg-cover",
  "bg-contain",
  "bg-center",
  "bg-top",
  "bg-right-top",
  "bg-right",
  "bg-right-bottom",
  "bg-bottom",
  "bg-left-bottom",
  "bg-left",
  "bg-left-top",
  // Gradients
  "bg-none",
  "bg-gradient-to-t",
  "bg-gradient-to-tr",
  "bg-gradient-to-r",
  "bg-gradient-to-br",
  "bg-gradient-to-b",
  "bg-gradient-to-bl",
  "bg-gradient-to-l",
  "bg-gradient-to-tl",
  // Filters
  "blur-none",
  "blur-sm",
  "blur",
  "blur-md",
  "blur-lg",
  "blur-xl",
  "blur-2xl",
  "blur-3xl",
  "brightness-0",
  "brightness-50",
  "brightness-75",
  "brightness-90",
  "brightness-95",
  "brightness-100",
  "brightness-105",
  "brightness-110",
  "brightness-125",
  "brightness-150",
  "brightness-200",
  "contrast-0",
  "contrast-50",
  "contrast-75",
  "contrast-100",
  "contrast-125",
  "contrast-150",
  "contrast-200",
  "grayscale-0",
  "grayscale",
  "hue-rotate-0",
  "hue-rotate-15",
  "hue-rotate-30",
  "hue-rotate-60",
  "hue-rotate-90",
  "hue-rotate-180",
  "-hue-rotate-15",
  "-hue-rotate-30",
  "-hue-rotate-60",
  "-hue-rotate-90",
  "-hue-rotate-180",
  "invert-0",
  "invert",
  "saturate-0",
  "saturate-50",
  "saturate-100",
  "saturate-150",
  "saturate-200",
  "sepia-0",
  "sepia",
  "drop-shadow-sm",
  "drop-shadow",
  "drop-shadow-md",
  "drop-shadow-lg",
  "drop-shadow-xl",
  "drop-shadow-2xl",
  "drop-shadow-none",
  // Backdrop filters
  "backdrop-blur-none",
  "backdrop-blur-sm",
  "backdrop-blur",
  "backdrop-blur-md",
  "backdrop-blur-lg",
  "backdrop-blur-xl",
  "backdrop-blur-2xl",
  "backdrop-blur-3xl",
  "backdrop-brightness-0",
  "backdrop-brightness-50",
  "backdrop-brightness-75",
  "backdrop-brightness-90",
  "backdrop-brightness-95",
  "backdrop-brightness-100",
  "backdrop-brightness-105",
  "backdrop-brightness-110",
  "backdrop-brightness-125",
  "backdrop-brightness-150",
  "backdrop-brightness-200",
  "backdrop-contrast-0",
  "backdrop-contrast-50",
  "backdrop-contrast-75",
  "backdrop-contrast-100",
  "backdrop-contrast-125",
  "backdrop-contrast-150",
  "backdrop-contrast-200",
  "backdrop-grayscale-0",
  "backdrop-grayscale",
  "backdrop-hue-rotate-0",
  "backdrop-hue-rotate-15",
  "backdrop-hue-rotate-30",
  "backdrop-hue-rotate-60",
  "backdrop-hue-rotate-90",
  "backdrop-hue-rotate-180",
  "backdrop-invert-0",
  "backdrop-invert",
  "backdrop-opacity-0",
  "backdrop-opacity-5",
  "backdrop-opacity-10",
  "backdrop-opacity-20",
  "backdrop-opacity-25",
  "backdrop-opacity-30",
  "backdrop-opacity-40",
  "backdrop-opacity-50",
  "backdrop-opacity-60",
  "backdrop-opacity-70",
  "backdrop-opacity-75",
  "backdrop-opacity-80",
  "backdrop-opacity-90",
  "backdrop-opacity-95",
  "backdrop-opacity-100",
  "backdrop-saturate-0",
  "backdrop-saturate-50",
  "backdrop-saturate-100",
  "backdrop-saturate-150",
  "backdrop-saturate-200",
  "backdrop-sepia-0",
  "backdrop-sepia",
  // Blend modes
  "mix-blend-normal",
  "mix-blend-multiply",
  "mix-blend-screen",
  "mix-blend-overlay",
  "mix-blend-darken",
  "mix-blend-lighten",
  "mix-blend-color-dodge",
  "mix-blend-color-burn",
  "mix-blend-hard-light",
  "mix-blend-soft-light",
  "mix-blend-difference",
  "mix-blend-exclusion",
  "mix-blend-hue",
  "mix-blend-saturation",
  "mix-blend-color",
  "mix-blend-luminosity",
  "mix-blend-plus-lighter",
  "bg-blend-normal",
  "bg-blend-multiply",
  "bg-blend-screen",
  "bg-blend-overlay",
  "bg-blend-darken",
  "bg-blend-lighten",
  "bg-blend-color-dodge",
  "bg-blend-color-burn",
  "bg-blend-hard-light",
  "bg-blend-soft-light",
  "bg-blend-difference",
  "bg-blend-exclusion",
  "bg-blend-hue",
  "bg-blend-saturation",
  "bg-blend-color",
  "bg-blend-luminosity",
  // Cursor
  "cursor-auto",
  "cursor-default",
  "cursor-pointer",
  "cursor-wait",
  "cursor-text",
  "cursor-move",
  "cursor-help",
  "cursor-not-allowed",
  "cursor-none",
  "cursor-context-menu",
  "cursor-progress",
  "cursor-cell",
  "cursor-crosshair",
  "cursor-vertical-text",
  "cursor-alias",
  "cursor-copy",
  "cursor-no-drop",
  "cursor-grab",
  "cursor-grabbing",
  "cursor-all-scroll",
  "cursor-col-resize",
  "cursor-row-resize",
  "cursor-n-resize",
  "cursor-e-resize",
  "cursor-s-resize",
  "cursor-w-resize",
  "cursor-ne-resize",
  "cursor-nw-resize",
  "cursor-se-resize",
  "cursor-sw-resize",
  "cursor-ew-resize",
  "cursor-ns-resize",
  "cursor-nesw-resize",
  "cursor-nwse-resize",
  "cursor-zoom-in",
  "cursor-zoom-out",
  // Pointer events
  "pointer-events-none",
  "pointer-events-auto",
  // Resize
  "resize-none",
  "resize-y",
  "resize-x",
  "resize",
  // Scroll behavior
  "scroll-auto",
  "scroll-smooth",
  // Scroll snap
  "snap-start",
  "snap-end",
  "snap-center",
  "snap-align-none",
  "snap-normal",
  "snap-always",
  "snap-none",
  "snap-x",
  "snap-y",
  "snap-both",
  "snap-mandatory",
  "snap-proximity",
  "snap-stop-normal",
  "snap-stop-always",
  // Touch action
  "touch-auto",
  "touch-none",
  "touch-pan-x",
  "touch-pan-left",
  "touch-pan-right",
  "touch-pan-y",
  "touch-pan-up",
  "touch-pan-down",
  "touch-pinch-zoom",
  "touch-manipulation",
  // Select
  "select-none",
  "select-text",
  "select-all",
  "select-auto",
  // Will change
  "will-change-auto",
  "will-change-scroll",
  "will-change-contents",
  "will-change-transform",
  // Visibility
  "visible",
  "invisible",
  "collapse",
  // Object fit
  "object-contain",
  "object-cover",
  "object-fill",
  "object-none",
  "object-scale-down",
  // Object position
  "object-bottom",
  "object-center",
  "object-left",
  "object-left-bottom",
  "object-left-top",
  "object-right",
  "object-right-bottom",
  "object-right-top",
  "object-top",
  // Aspect ratio
  "aspect-auto",
  "aspect-square",
  "aspect-video",
  // Appearance
  "appearance-none",
  "appearance-auto",
  // Transition
  "transition-none",
  "transition-all",
  "transition",
  "transition-colors",
  "transition-opacity",
  "transition-shadow",
  "transition-transform",
  "duration-75",
  "duration-100",
  "duration-150",
  "duration-200",
  "duration-300",
  "duration-500",
  "duration-700",
  "duration-1000",
  "ease-linear",
  "ease-in",
  "ease-out",
  "ease-in-out",
  "delay-0",
  "delay-75",
  "delay-100",
  "delay-150",
  "delay-200",
  "delay-300",
  "delay-500",
  "delay-700",
  "delay-1000",
  // Animation
  "animate-none",
  "animate-spin",
  "animate-ping",
  "animate-pulse",
  "animate-bounce",
  // Transform
  "scale-0",
  "scale-50",
  "scale-75",
  "scale-90",
  "scale-95",
  "scale-100",
  "scale-105",
  "scale-110",
  "scale-125",
  "scale-150",
  "scale-x-0",
  "scale-x-50",
  "scale-x-75",
  "scale-x-90",
  "scale-x-95",
  "scale-x-100",
  "scale-x-105",
  "scale-x-110",
  "scale-x-125",
  "scale-x-150",
  "scale-y-0",
  "scale-y-50",
  "scale-y-75",
  "scale-y-90",
  "scale-y-95",
  "scale-y-100",
  "scale-y-105",
  "scale-y-110",
  "scale-y-125",
  "scale-y-150",
  "rotate-0",
  "rotate-1",
  "rotate-2",
  "rotate-3",
  "rotate-6",
  "rotate-12",
  "rotate-45",
  "rotate-90",
  "rotate-180",
  "-rotate-1",
  "-rotate-2",
  "-rotate-3",
  "-rotate-6",
  "-rotate-12",
  "-rotate-45",
  "-rotate-90",
  "-rotate-180",
  // Transform origin
  "origin-center",
  "origin-top",
  "origin-top-right",
  "origin-right",
  "origin-bottom-right",
  "origin-bottom",
  "origin-bottom-left",
  "origin-left",
  "origin-top-left",
  // Skew
  "skew-x-0",
  "skew-x-1",
  "skew-x-2",
  "skew-x-3",
  "skew-x-6",
  "skew-x-12",
  "-skew-x-1",
  "-skew-x-2",
  "-skew-x-3",
  "-skew-x-6",
  "-skew-x-12",
  "skew-y-0",
  "skew-y-1",
  "skew-y-2",
  "skew-y-3",
  "skew-y-6",
  "skew-y-12",
  "-skew-y-1",
  "-skew-y-2",
  "-skew-y-3",
  "-skew-y-6",
  "-skew-y-12",
  // Fill / Stroke (SVG)
  "fill-none",
  "stroke-none",
  "stroke-0",
  "stroke-1",
  "stroke-2",
  // Screen reader
  "sr-only",
  "not-sr-only",
  // Sizing extras
  "w-auto",
  "w-full",
  "w-screen",
  "w-min",
  "w-max",
  "w-fit",
  "min-w-0",
  "min-w-full",
  "min-w-min",
  "min-w-max",
  "min-w-fit",
  "max-w-0",
  "max-w-none",
  "max-w-xs",
  "max-w-sm",
  "max-w-md",
  "max-w-lg",
  "max-w-xl",
  "max-w-2xl",
  "max-w-3xl",
  "max-w-4xl",
  "max-w-5xl",
  "max-w-6xl",
  "max-w-7xl",
  "max-w-full",
  "max-w-min",
  "max-w-max",
  "max-w-fit",
  "max-w-prose",
  "max-w-screen-sm",
  "max-w-screen-md",
  "max-w-screen-lg",
  "max-w-screen-xl",
  "max-w-screen-2xl",
  "h-auto",
  "h-full",
  "h-screen",
  "h-min",
  "h-max",
  "h-fit",
  "min-h-0",
  "min-h-full",
  "min-h-screen",
  "min-h-min",
  "min-h-max",
  "min-h-fit",
  "max-h-none",
  "max-h-full",
  "max-h-screen",
  "max-h-min",
  "max-h-max",
  "max-h-fit",
  // Size (w+h shorthand)
  "size-auto",
  "size-full",
  "size-min",
  "size-max",
  "size-fit",
];

// ============================================================================
// Lazy-generated class list (built once on first search)
// ============================================================================

let _allClasses: string[] | null = null;

function generateAllClasses(): string[] {
  const classes: string[] = [...STATIC_CLASSES];

  // Color classes: text-red-500, bg-blue-200, etc.
  for (const prefix of COLOR_PREFIXES) {
    for (const color of TAILWIND_COLOR_ORDER) {
      for (const shade of TAILWIND_SHADES) {
        classes.push(`${prefix}-${color}-${shade}`);
      }
    }
    for (const special of SPECIAL_COLORS) {
      classes.push(`${prefix}-${special}`);
    }
  }

  // Spacing classes: m-4, px-2, gap-6, scroll-m-4, indent-8, etc.
  for (const prefix of SPACING_PREFIXES) {
    for (const size of SPACING_SIZES) {
      classes.push(`${prefix}-${size}`);
    }
    // Negative spacing for margin, positioning, and indent
    if (
      [
        "m",
        "mx",
        "my",
        "mt",
        "mr",
        "mb",
        "ml",
        "ms",
        "me",
        "inset",
        "inset-x",
        "inset-y",
        "top",
        "right",
        "bottom",
        "left",
        "start",
        "end",
        "indent",
      ].includes(prefix)
    ) {
      for (const size of SPACING_SIZES) {
        if (size !== "0" && size !== "px" && size !== "auto") {
          classes.push(`-${prefix}-${size}`);
        }
      }
    }
  }

  // Text sizes: text-xs, text-sm, text-base, etc.
  for (const size of TEXT_SIZES) {
    classes.push(`text-${size}`);
  }

  // Font weights: font-thin, font-bold, etc.
  for (const weight of FONT_WEIGHTS) {
    classes.push(`font-${weight}`);
  }

  // Flex basis: basis-0, basis-4, basis-1/2, etc.
  for (const size of SPACING_SIZES) {
    classes.push(`basis-${size}`);
  }
  for (const frac of [
    "1/2",
    "1/3",
    "2/3",
    "1/4",
    "2/4",
    "3/4",
    "1/5",
    "2/5",
    "3/5",
    "4/5",
    "1/6",
    "2/6",
    "3/6",
    "4/6",
    "5/6",
    "1/12",
    "2/12",
    "3/12",
    "4/12",
    "5/12",
    "6/12",
    "7/12",
    "8/12",
    "9/12",
    "10/12",
    "11/12",
  ]) {
    classes.push(`basis-${frac}`);
  }

  // Width: w-0, w-4, w-1/2, etc.
  for (const size of WIDTH_SIZES) {
    classes.push(`w-${size}`);
  }

  // Height: h-0, h-4, h-1/2, etc.
  for (const size of HEIGHT_SIZES) {
    classes.push(`h-${size}`);
  }

  // Size (w+h): size-0, size-4, etc.
  for (const size of SPACING_SIZES) {
    classes.push(`size-${size}`);
  }

  // Translate: translate-x-0, translate-y-4, etc.
  for (const size of SPACING_SIZES) {
    classes.push(`translate-x-${size}`);
    classes.push(`translate-y-${size}`);
    if (size !== "0" && size !== "px") {
      classes.push(`-translate-x-${size}`);
      classes.push(`-translate-y-${size}`);
    }
  }
  for (const frac of ["1/2", "1/3", "2/3", "1/4", "2/4", "3/4", "full"]) {
    classes.push(`translate-x-${frac}`);
    classes.push(`translate-y-${frac}`);
    classes.push(`-translate-x-${frac}`);
    classes.push(`-translate-y-${frac}`);
  }

  // Border radius: rounded, rounded-md, etc.
  for (const r of BORDER_RADIUS) {
    const suffix = r ? `-${r}` : "";
    classes.push(`rounded${suffix}`);
    classes.push(`rounded-t${suffix}`);
    classes.push(`rounded-r${suffix}`);
    classes.push(`rounded-b${suffix}`);
    classes.push(`rounded-l${suffix}`);
    classes.push(`rounded-tl${suffix}`);
    classes.push(`rounded-tr${suffix}`);
    classes.push(`rounded-br${suffix}`);
    classes.push(`rounded-bl${suffix}`);
    classes.push(`rounded-s${suffix}`);
    classes.push(`rounded-e${suffix}`);
    classes.push(`rounded-ss${suffix}`);
    classes.push(`rounded-se${suffix}`);
    classes.push(`rounded-es${suffix}`);
    classes.push(`rounded-ee${suffix}`);
  }

  // Opacity: opacity-0, opacity-50, opacity-100, etc.
  for (const v of OPACITY_VALUES) {
    classes.push(`opacity-${v}`);
  }

  // Z-index: z-0, z-10, z-50, z-auto
  for (const z of Z_INDEX) {
    classes.push(`z-${z}`);
  }

  // Deduplicate
  return [...new Set(classes)].sort();
}

function getAllClasses(): string[] {
  if (!_allClasses) {
    _allClasses = generateAllClasses();
  }
  return _allClasses;
}

// ============================================================================
// Search API
// ============================================================================

const RESPONSIVE_PREFIXES = ["sm", "md", "lg", "xl", "2xl"];
const STATE_PREFIXES = [
  "hover",
  "focus",
  "focus-within",
  "focus-visible",
  "active",
  "visited",
  "target",
  "first",
  "last",
  "only",
  "odd",
  "even",
  "first-of-type",
  "last-of-type",
  "only-of-type",
  "empty",
  "disabled",
  "enabled",
  "checked",
  "indeterminate",
  "default",
  "required",
  "valid",
  "invalid",
  "in-range",
  "out-of-range",
  "placeholder-shown",
  "autofill",
  "read-only",
  "open",
  "group-hover",
  "group-focus",
  "group-active",
  "group-open",
  "peer-hover",
  "peer-focus",
  "peer-checked",
  "peer-disabled",
  "dark",
  "print",
  "portrait",
  "landscape",
  "motion-safe",
  "motion-reduce",
  "contrast-more",
  "contrast-less",
  "forced-colors",
];

/**
 * Search Tailwind classes matching a query token.
 * Returns up to `limit` matches, prioritizing prefix matches.
 */
function searchTailwindClasses(query: string, limit = 50): string[] {
  if (!query || query.length < 2) return [];

  const allClasses = getAllClasses();
  const q = query.toLowerCase();

  // Strip responsive/state prefix for search (e.g., "md:text-r" → "text-r")
  let searchToken = q;
  let prefix = "";
  const colonIdx = q.lastIndexOf(":");
  if (colonIdx > 0) {
    const beforeColon = q.slice(0, colonIdx);
    const possiblePrefix = beforeColon.split(":").pop() || "";
    if (
      RESPONSIVE_PREFIXES.includes(possiblePrefix) ||
      STATE_PREFIXES.includes(possiblePrefix)
    ) {
      prefix = q.slice(0, colonIdx + 1);
      searchToken = q.slice(colonIdx + 1);
    }
  }

  if (searchToken.length < 2) return [];

  // Split results: exact prefix matches first, then contains matches
  const prefixMatches: string[] = [];
  const containsMatches: string[] = [];

  for (const cls of allClasses) {
    if (prefixMatches.length + containsMatches.length >= limit) break;

    if (cls.startsWith(searchToken)) {
      prefixMatches.push(prefix + cls);
    } else if (cls.includes(searchToken)) {
      containsMatches.push(prefix + cls);
    }
  }

  return [...prefixMatches, ...containsMatches].slice(0, limit);
}

/**
 * Get the current token being typed at cursor position in a class string.
 * Returns the token and its start/end positions.
 */
function getCurrentToken(
  value: string,
  cursorPosition: number,
): { token: string; start: number; end: number } {
  // Find token boundaries around cursor
  let start = cursorPosition;
  let end = cursorPosition;

  // Walk backwards to find start of token
  while (start > 0 && value[start - 1] !== " ") {
    start--;
  }

  // Walk forwards to find end of token
  while (end < value.length && value[end] !== " ") {
    end++;
  }

  return {
    token: value.slice(start, end),
    start,
    end,
  };
}

/**
 * Replace a token in the class string with a new value.
 */
function replaceToken(
  value: string,
  start: number,
  end: number,
  replacement: string,
): string {
  return value.slice(0, start) + replacement + value.slice(end);
}

export { searchTailwindClasses, getCurrentToken, replaceToken };
